/**
 * Business trips of a sole trader (§ 4 Abs 5 EStG): Kilometergeld for a private vehicle, Tagesgeld
 * and Nächtigungsgeld without receipts. Each rate carries the day it applies from - a trip is
 * paid at the rate of its day - and each was checked in RIS on 31 Oct 2026.
 *
 *  - **Kilometergeld** (Kilometergeldverordnung, BGBl II 289/2024; § 10 RGV): only for a vehicle
 *    that is not business property (used 50 % or less for the business). 0,42 €/km for a car
 *    until 2024, 0,50 € from 1 January 2025 (BGBl I 144/2024). Motorcycle and bicycle rose to
 *    0,50 € with it and fell to 0,25 € on 1 July 2025 (BGBl I 26/2025). At most 30.000 km a year
 *    for cars and motorcycles together, 3.000 km for a bicycle (KmGV § 4); a trip beyond the cap
 *    gets nothing for the kilometres over it.
 *  - **Tagesgeld** (§ 26 Z 4 lit b EStG): 30 € for 24 hours from 2025 (26,40 € before). A trip of
 *    more than three hours gets 1/12 per started hour, the full amount from 11 hours on. A
 *    business meal paid by someone else takes 15 € off (13,20 € before 2025), never below 0.
 *    Only from 25 km away from the place of work, and not after five days at the same place -
 *    which the person confirms, since the app cannot know it.
 *  - **Nächtigungsgeld** (§ 26 Z 4 lit c EStG): 17 € per night without a receipt (15 € before
 *    2025), breakfast included. A hotel bill is a receipt and booked as one instead.
 *
 * Amounts are whole cents.
 */

export type Vehicle = "car" | "motorcycle" | "bicycle" | "none";

export type TripInput = {
  /** ISO day the trip starts, and the day it ends (empty for the same day). */
  date: string;
  endDate?: string;
  /** "07:30": the time of departure and of return, local. */
  departureTime?: string;
  returnTime?: string;
  vehicle: Vehicle;
  /** Kilometres driven for the business, both ways. */
  distanceKm?: number;
  /** Tagesgeld claimed: the person confirms 25 km and the five-day rule. */
  perDiem?: boolean;
  /** Business meals someone else paid for. */
  meals?: number;
  /** Nights without a hotel receipt. */
  nights?: number;
};

type Rates = { from: string; car: number; motorcycle: number; bicycle: number };

/** Kilometergeld per km in cents, newest last. */
const MILEAGE: Rates[] = [
  { from: "0000-01-01", car: 42, motorcycle: 24, bicycle: 38 },
  { from: "2025-01-01", car: 50, motorcycle: 50, bicycle: 50 },
  { from: "2025-07-01", car: 50, motorcycle: 25, bicycle: 25 },
];

/** Tagesgeld per 24 hours, the meal reduction and Nächtigungsgeld, in cents. */
const PER_DIEM = [
  { from: "0000-01-01", day: 2640, meal: 1320, night: 1500 },
  { from: "2025-01-01", day: 3000, meal: 1500, night: 1700 },
];

/** Kilometres a year Kilometergeld is paid for: cars and motorcycles together, bicycles. */
export const MOTOR_KM_CAP = 30_000;
export const BICYCLE_KM_CAP = 3_000;

function at<T extends { from: string }>(table: T[], date: string): T {
  return [...table].reverse().find((row) => row.from <= date.slice(0, 10)) ?? table[0]!;
}

/** The Kilometergeld per km on a day, in cents. */
export function mileageRate(vehicle: Vehicle, date: string): number {
  return vehicle === "none" ? 0 : at(MILEAGE, date)[vehicle];
}

/** Hours from departure to return; 0 when a time is missing. */
export function tripHours(trip: TripInput): number {
  if (!trip.departureTime || !trip.returnTime) return 0;
  const start = Date.parse(`${trip.date.slice(0, 10)}T${trip.departureTime}:00Z`);
  const end = Date.parse(`${(trip.endDate || trip.date).slice(0, 10)}T${trip.returnTime}:00Z`);
  return Number.isFinite(start) && Number.isFinite(end) && end > start
    ? (end - start) / 3_600_000
    : 0;
}

/**
 * Tagesgeld in cents: 30 € for each full 24 hours, and for the hours left 1/12 per started hour
 * once they are more than three, never more than a full day. Meals reduce it.
 */
export function perDiemAmount(trip: TripInput): number {
  if (!trip.perDiem) return 0;
  const rates = at(PER_DIEM, trip.date);
  const hours = tripHours(trip);
  const days = Math.floor(hours / 24);
  const rest = hours - days * 24;
  const twelfths = rest > 3 ? Math.min(12, Math.ceil(rest - 1e-9)) : 0;
  const amount = days * rates.day + Math.round((rates.day * twelfths) / 12);
  return Math.max(0, amount - Math.max(0, trip.meals ?? 0) * rates.meal);
}

/** Nächtigungsgeld in cents. */
export function lodgingAmount(trip: TripInput): number {
  return Math.max(0, Math.floor(trip.nights ?? 0)) * at(PER_DIEM, trip.date).night;
}

export type TripAmounts = {
  /** Kilometres paid, after the year's cap. */
  paidKm: number;
  mileage: number;
  perDiem: number;
  lodging: number;
  total: number;
};

/**
 * What one trip is worth, given the kilometres of the same kind already paid this year (cars and
 * motorcycles share one cap, bicycles have their own).
 */
export function tripAmounts(trip: TripInput, kmBefore = 0): TripAmounts {
  const km = trip.vehicle === "none" ? 0 : Math.max(0, trip.distanceKm ?? 0);
  const cap = trip.vehicle === "bicycle" ? BICYCLE_KM_CAP : MOTOR_KM_CAP;
  const paidKm = Math.max(0, Math.min(km, cap - kmBefore));
  const mileage = Math.round(paidKm * mileageRate(trip.vehicle, trip.date));
  const perDiem = perDiemAmount(trip);
  const lodging = lodgingAmount(trip);
  return { paidKm, mileage, perDiem, lodging, total: mileage + perDiem + lodging };
}

/** The cap a vehicle counts against. */
export const capGroup = (vehicle: Vehicle) => (vehicle === "bicycle" ? "bicycle" : "motor");

/**
 * Kilometres already driven under the same cap in the trip's calendar year before it: earlier
 * days first, the same day by `id`, so the order trips were loaded in does not matter.
 */
export function kmBefore<T extends TripInput & { id: string }>(trip: T, trips: T[]): number {
  if (trip.vehicle === "none") return 0;
  const day = trip.date.slice(0, 10);
  return trips
    .filter(
      (other) =>
        other.id !== trip.id &&
        other.vehicle !== "none" &&
        capGroup(other.vehicle) === capGroup(trip.vehicle) &&
        other.date.slice(0, 4) === day.slice(0, 4) &&
        (other.date.slice(0, 10) < day || (other.date.slice(0, 10) === day && other.id < trip.id)),
    )
    .reduce((sum, other) => sum + Math.max(0, other.distanceKm ?? 0), 0);
}

/** Every trip with its amounts, the year's cap applied (`kmBefore`), in date order. */
export function travelLog<T extends TripInput & { id: string }>(
  trips: T[],
): Array<T & { amounts: TripAmounts; overCap: boolean }> {
  return trips
    .map((trip) => {
      const amounts = tripAmounts(trip, kmBefore(trip, trips));
      const km = trip.vehicle === "none" ? 0 : Math.max(0, trip.distanceKm ?? 0);
      return { ...trip, amounts, overCap: amounts.paidKm < km };
    })
    .sort(
      (a, b) => a.date.slice(0, 10).localeCompare(b.date.slice(0, 10)) || a.id.localeCompare(b.id),
    );
}
